import React, { useCallback, useEffect, useRef, useState } from 'react';

const ChevronLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);
const ChevronRight = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);
const PauseIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
  </svg>
);
const PlayIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const controlClass =
  'w-11 h-11 shrink-0 inline-flex items-center justify-center rounded-full bg-[#00213a] text-white border-2 border-white/80 shadow-lg hover:bg-[#003a5d] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#fdbb2e] transition-colors duration-150';

/**
 * Full-width photo slideshow for the homepage hero.
 *
 * slides: [{ src, thumb, alt, label, fit?: 'cover' | 'contain', pos?: CSS object-position }]
 *  - 'cover' fills the banner (use for large landscape photos).
 *  - 'contain' shows a smaller or portrait photo whole, centered over a blurred
 *    copy of itself, so it is never stretched or cropped.
 *
 * Accessibility: auto-rotates only when the visitor has not asked for reduced
 * motion, always has a Pause/Play button (WCAG 2.2.2), stops while hovered or
 * focused, supports Left/Right arrow keys, and off-screen slides are `inert`
 * and aria-hidden so only the visible photo is announced.
 */
const HeroSlideshow = ({ slides, label, interval = 6500 }) => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef(null);
  const stripRef = useRef(null);
  const count = slides.length;

  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPlaying(false);
    }
  }, []);

  const go = useCallback((i) => setIndex(((i % count) + count) % count), [count]);

  useEffect(() => {
    if (!playing || paused || count < 2) return undefined;
    const t = setTimeout(() => go(index + 1), interval);
    return () => clearTimeout(t);
  }, [playing, paused, index, count, interval, go]);

  // keep the active thumbnail visible in the strip (scroll the strip only, never the page)
  useEffect(() => {
    const strip = stripRef.current;
    const btn = strip && strip.children[index];
    if (!strip || !btn) return;
    const left = btn.offsetLeft - strip.clientWidth / 2 + btn.clientWidth / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
  }, [index]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
  };

  return (
    <div
      ref={rootRef}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => { if (!rootRef.current.contains(e.relatedTarget)) setPaused(false); }}
      onKeyDown={onKeyDown}
      className="absolute inset-0 bg-[#00213a]"
    >
      <div className="absolute inset-0" aria-live={playing && !paused ? 'off' : 'polite'}>
        {slides.map((s, i) => {
          const active = i === index;
          const contain = s.fit === 'contain';
          return (
            <div
              key={s.src}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}`}
              aria-hidden={active ? undefined : true}
              inert={!active}
              className={`absolute inset-0 transition-opacity duration-700 motion-reduce:transition-none ${active ? 'opacity-100' : 'opacity-0'}`}
            >
              {contain && (
                <img
                  src={s.src}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
                  loading="lazy"
                />
              )}
              <img
                src={s.src}
                alt={s.alt}
                className={`relative w-full h-full ${contain ? 'object-contain' : 'object-cover'}`}
                style={!contain && s.pos ? { objectPosition: s.pos } : undefined}
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchPriority={i === 0 ? 'high' : undefined}
              />
            </div>
          );
        })}
      </div>

      {/* Controls + thumbnail strip */}
      <div className="absolute inset-x-0 bottom-0 bg-[#00213a]/85 px-3 py-3 sm:px-5">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-center gap-3 lg:flex-nowrap lg:justify-start">
          <div className="flex items-center gap-2">
            <button type="button" className={controlClass} onClick={() => go(index - 1)} aria-label="Previous photo">
              <ChevronLeft />
            </button>
            <button
              type="button"
              className={controlClass}
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
            >
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button type="button" className={controlClass} onClick={() => go(index + 1)} aria-label="Next photo">
              <ChevronRight />
            </button>
          </div>

          {/* Dots on narrow screens */}
          <div className="flex flex-wrap items-center justify-center lg:hidden" role="group" aria-label="Choose a photo">
            {slides.map((s, i) => (
              <button
                key={s.src}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show photo ${i + 1} of ${count}: ${s.label}`}
                aria-current={i === index ? 'true' : undefined}
                className="w-6 h-6 inline-flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-[#fdbb2e]"
              >
                <span
                  aria-hidden="true"
                  className={`block rounded-full transition-all duration-200 ${i === index ? 'w-3.5 h-3.5 bg-[#fdbb2e]' : 'w-2.5 h-2.5 bg-white/80'}`}
                />
              </button>
            ))}
          </div>

          {/* Thumbnails on wide screens */}
          <div
            ref={stripRef}
            className="hidden lg:flex flex-1 min-w-0 items-center justify-end gap-2 overflow-x-auto py-1 px-1"
            role="group"
            aria-label="Choose a photo"
          >
            {slides.map((s, i) => (
              <button
                key={s.src}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show photo ${i + 1} of ${count}: ${s.label}`}
                aria-current={i === index ? 'true' : undefined}
                className={`shrink-0 w-24 h-16 overflow-hidden rounded-sm border-2 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#fdbb2e] transition-all duration-200 ${
                  i === index ? 'border-[#fdbb2e] opacity-100' : 'border-white/70 opacity-80 hover:opacity-100'
                }`}
              >
                <img src={s.thumb} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSlideshow;
